"use client";

import React, { lazy, Suspense, useCallback, useRef, useState } from "react";
import { XIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/lib/components/ui/button";
import { DropdownMenuLabel } from "@/lib/components/ui/dropdown-menu";
import { useWidget } from "@/lib/hooks/use-widget";
import { useTotalBalance } from "@/lib/hooks/use-total-balance";

const UserAvatar = lazy(() => import("@/lib/profile/user-avatar"));
const UserWallet = lazy(() => import("@/lib/profile/user-wallet"));
const UserDetails = lazy(() => import("@/lib/profile/user-details"));
const TotalBalance = lazy(() => import("@/lib/profile/total-balance"));
const ShortcutButtons = lazy(() => import("@/lib/profile/shortcut-buttons"));
const BottomButtons = lazy(() => import("@/lib/profile/bottom-buttons"));

function UserButton() {
  const { setWidgetOpen, widgetOpen } = useWidget();
  const totalBalance = useTotalBalance();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const [popupPos, setPopupPos] = useState<{
    top: number;
    right: number;
  } | null>(null);

  const handleClick = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();

      if (window?.innerWidth < 640) {
        setPopupPos(null);
        setWidgetOpen(v => !v);
        return;
      }

      setPopupPos({
        top: rect.top, // same top as the button
        right: window.innerWidth - rect.right, // from the right edge of the viewport
      });
      setWidgetOpen(v => !v);
    }
  }, [setWidgetOpen]);

  return (
    <div className="relative flex items-center justify-center">
      <Button
        variant="ghost"
        className="h-8 w-8 p-0 rounded-sm cursor-pointer focus-visible:ring-0 focus-visible:outline-none"
        onClick={handleClick}
        ref={buttonRef}
      >
        <Suspense>
          <UserAvatar />
        </Suspense>
      </Button>

      {widgetOpen && (
        <div
          className={cn(
            "fixed z-[9999] h-screen w-screen flex flex-col bg-dune text-black sm:h-auto sm:w-xs sm:max-w-xs sm:rounded-md top-0 sm:left-auto left-0",
          )}
          style={{
            top: popupPos?.top,
            right: popupPos?.right,
          }}
        >
          <DropdownMenuLabel className="relative flex justify-between items-start px-3 pt-3">
            <Suspense>
              <UserDetails />
            </Suspense>
            <button
              className="h-6 w-6 cursor-pointer"
              onClick={() => setWidgetOpen(false)}
            >
              <XIcon />
            </button>
          </DropdownMenuLabel>

          <DropdownMenuLabel className="p-3">
            <Suspense>
              <TotalBalance />
            </Suspense>
          </DropdownMenuLabel>

          <DropdownMenuLabel
            className={cn(
              "p-4 pt-0 grid gap-2",
              totalBalance > 0 ? "grid-cols-5" : "grid-cols-2",
            )}
          >
            <Suspense>
              <ShortcutButtons />
            </Suspense>
          </DropdownMenuLabel>

          <DropdownMenuLabel className="px-3 py-2 bg-white flex-auto sm:max-h-72 overflow-y-auto">
            <Suspense>
              <UserWallet />
            </Suspense>
          </DropdownMenuLabel>
          <DropdownMenuLabel asChild className="p-4 rounded-b-md">
            <div className="flex gap-2 bg-white shadow-[0_0.1875rem_0.625rem_0_hsla(0,0%,0%,0.15)]">
              <Suspense>
                <BottomButtons />
              </Suspense>
            </div>
          </DropdownMenuLabel>

          <div
            className="fixed top-0 left-0 w-screen h-screen -z-10"
            onClick={() => setWidgetOpen(false)}
          />
        </div>
      )}
    </div>
  );
}

export default UserButton;
